"use client";

import { create } from "zustand";
import { toast } from "@/shared/components/toast";
import { getApiErrorMessage } from "@/shared/helpers/errors";
import { roundBRL } from "@/shared/helpers";
import { useActiveStore } from "@/modules/store/stores/useActiveStore";
import { track } from "@/modules/tracking/lib/events";
import { diffCart } from "@/modules/tracking/lib/items";
import * as cartService from "../services/cart.service";
import type { CartDto } from "../types/cart.dto";

interface CartState {
    isOpen: boolean;
    /** Carrinho do backend (fonte da verdade). null = vazio ou não carregado. */
    cart: CartDto | null;
    /** true enquanto uma mutação está em voo — bloqueia novas. */
    pending: boolean;
    /** Chave (cliente, loja) já carregada — evita refazer o GET que o bootstrap trouxe. */
    loadedKey: string | null;

    open: () => void;
    close: () => void;
    toggle: () => void;

    /**
     * Re-busca o carrinho. `trackChanges`: o servidor mexeu no carrinho a
     * pedido do cliente (comprar de novo, chat) — mede o que entrou/saiu. As
     * sincronizações de fundo (troca de loja, login) não passam isto.
     */
    refresh: (options?: { trackChanges?: boolean }) => Promise<void>;
    /** O bootstrap já trouxe o carrinho desta chave — semeia sem ir ao backend. */
    adopt: (key: string, cart: CartDto | null) => void;
    /**
     * Adiciona (ou soma) o produto na loja ativa.
     * `options`: variante escolhida (JSON string). `comment`: observação do
     * cliente para o item (só em produto com `allowObservation`). `silent`
     * pula drawer e toast.
     */
    add: (
        productId: number,
        quantity?: number,
        extra?: { options?: string; comment?: string; silent?: boolean }
    ) => Promise<void>;
    updateQuantity: (productId: number, quantity: number, silent?: boolean) => Promise<void>;
    updateComment: (productId: number, comment: string) => Promise<void>;
    remove: (productId: number, silent?: boolean) => Promise<void>;
    clear: () => Promise<void>;
}

/**
 * Loja ativa já resolvida, ou `null` enquanto o StoreInit não terminou. Não
 * existe carrinho sem loja: o backend exige `storeId > 0` em toda operação.
 */
function resolvedStoreId(): number | null {
    const { storeId, ready } = useActiveStore.getState();
    return ready && storeId > 0 ? storeId : null;
}

/** add_to_cart / remove_from_cart pelo que mudou no carrinho devolvido pela API. */
function trackCartChanges(before: CartDto | null, after: CartDto | null): void {
    const { added, removed } = diffCart(before, after);
    if (added.length) track({ name: "add_to_cart", items: added });
    if (removed.length) track({ name: "remove_from_cart", items: removed });
}

/** Observação em voo — "Finalizar a compra" espera ela chegar antes de sair do carrinho. */
let pendingCommentSave: Promise<void> | null = null;

export function waitForPendingCommentSave(): Promise<void> {
    return pendingCommentSave ?? Promise.resolve();
}

/**
 * Carrinho sincronizado com a API (/cart), identificado pelo x-session-id e
 * mesclado ao cliente quando autenticado. É por loja (`ecm__cart.store_id`).
 * Toda mutação usa o CartDto retornado como novo estado.
 */
export const useCartStore = create<CartState>()((set, get) => {
    /** Executa uma mutação serializada e aplica o carrinho retornado. */
    async function mutate(request: (storeId: number) => Promise<CartDto | null>): Promise<boolean> {
        const storeId = resolvedStoreId();
        if (storeId === null) {
            toast.error("Selecione uma loja para montar seu carrinho.");
            return false;
        }
        if (get().pending) return false;
        set({ pending: true });
        try {
            const before = get().cart;
            const after = await request(storeId);
            set({ cart: after });
            // Medição pelo que a API devolveu: mede o que entrou de fato.
            trackCartChanges(before, after);
            return true;
        } catch (error) {
            toast.error(
                getApiErrorMessage(error, "Não foi possível atualizar o carrinho. Tente novamente.")
            );
            return false;
        } finally {
            set({ pending: false });
        }
    }

    return {
        isOpen: false,
        cart: null,
        pending: false,
        loadedKey: null,

        open: () => set({ isOpen: true }),
        close: () => set({ isOpen: false }),
        toggle: () => set((s) => ({ isOpen: !s.isOpen })),

        adopt: (key, cart) => set({ cart, loadedKey: key }),

        refresh: async ({ trackChanges = false } = {}) => {
            // Busca de fundo: sem loja resolvida, o useCartSync chama de novo depois.
            const storeId = resolvedStoreId();
            if (storeId === null) return;
            try {
                const before = get().cart;
                const after = await cartService.getCart(storeId);
                set({ cart: after });
                if (trackChanges) trackCartChanges(before, after);
            } catch {
                // API indisponível — mantém o estado atual.
            }
        },

        add: async (productId, quantity = 1, { options, comment, silent } = {}) => {
            // Só abre o drawer se a API aceitou o item (ex.: sem estoque, nada foi adicionado).
            // Observação vazia não viaja: a nest grava o que recebe.
            const ok = await mutate((storeId) =>
                cartService.addCartProduct(productId, {
                    quantity,
                    storeId,
                    options,
                    comment: comment?.trim() || undefined,
                })
            );
            if (ok && !silent) {
                set({ isOpen: true });
                toast.neutral("Produto adicionado ao carrinho!");
            }
        },

        updateQuantity: async (productId, quantity, silent) => {
            if (quantity <= 0) return get().remove(productId, silent);

            // Item removido por fora (drawer, outra aba): "que haja N deste produto" vira add.
            const inCart = get().cart?.items?.some((item) => item.productId === productId) ?? false;
            if (!inCart) {
                await get().add(productId, quantity, { silent: true });
                if (!silent) toast.neutral("Carrinho atualizado!");
                return;
            }

            const ok = await mutate((storeId) =>
                cartService.updateCartItem(productId, quantity, storeId)
            );
            if (ok && !silent) toast.neutral("Carrinho atualizado!");
        },

        // Fora do `mutate`: salva no blur, e o `pending` dele descartaria o clique
        // (ex.: no "+") que tirou o foco do campo.
        updateComment: async (productId, comment) => {
            const storeId = resolvedStoreId();
            if (storeId === null) return;

            const save = cartService
                .updateCartItemComment(productId, comment, storeId)
                .then((cart) => set({ cart }))
                .catch((error) => {
                    toast.error(
                        getApiErrorMessage(error, "Não foi possível salvar a observação. Tente novamente.")
                    );
                });
            pendingCommentSave = save;
            await save;
        },

        remove: async (productId, silent) => {
            const ok = await mutate((storeId) => cartService.removeCartItem(productId, storeId));
            if (ok && !silent) toast.neutral("Produto removido do carrinho!");
        },

        clear: async () => {
            await mutate((storeId) => cartService.clearCart(storeId).then(() => null));
        },
    };
});

/**
 * Sinal efêmero de POR QUE o drawer está fechando: "Finalizar compra" marca
 * "checkout" antes do close(); quem reage ao fechamento lê e limpa o sinal.
 */
let cartCloseIntent: "checkout" | null = null;

export function markCartCloseForCheckout(): void {
    cartCloseIntent = "checkout";
}

export function consumeCartCloseIntent(): "checkout" | null {
    const intent = cartCloseIntent;
    cartCloseIntent = null;
    return intent;
}

// Referência estável para o seletor não recriar [] a cada render.
const NO_ITEMS: CartDto["items"] = [];

export const useCartItems = () => useCartStore((s) => s.cart?.items ?? NO_ITEMS);

/**
 * Quantas coisas há no carrinho, sempre INTEIRO: produto de contagem soma as
 * unidades; produto por peso conta 1 por linha (somar 0,16 kg ao total dava um
 * badge "0.15"). ANCHOR: cartItemCount
 */
export function cartItemCount(items: ReadonlyArray<{ quantity: number }>): number {
    return items.reduce((acc, item) => acc + (Number.isInteger(item.quantity) ? item.quantity : 1), 0);
}

/** Contagem do badge do header — ver `cartItemCount`. */
export const useCartCount = () => useCartStore((s) => cartItemCount(s.cart?.items ?? NO_ITEMS));

/**
 * Subtotal do backend quando disponível (ele conhece promoção e regra de
 * preço), com soma local só como fallback. Fonte única de todas as etapas.
 */
export const useCartSubtotal = () =>
    useCartStore(
        (s) =>
            s.cart?.subtotal ??
            roundBRL(
                (s.cart?.items ?? NO_ITEMS).reduce(
                    (sum, i) => sum + (i.unitPrice ?? 0) * i.quantity,
                    0
                )
            )
    );

/** Desconto do cupom aplicado (0 sem cupom). Fonte única de todas as etapas. */
export const useCartDiscount = () =>
    useCartStore((s) => (s.cart?.appliedCoupon ? (s.cart.discount ?? 0) : 0));
